import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { variantLabel } from "@/lib/format";
import * as productApi from "@/lib/api/products";
import { firstRelation } from "@/lib/relations";

interface Props {
  productId: string;
  orgId: string;
  parentVariantId: string;
  onChanged: () => void;
}

interface AvailabilityLeaf {
  component_variant_id: string;
  sku: string | null;
  product_name: string | null;
  qty_per_unit: number;
  available: number;
  units_possible: number;
}

interface AvailabilityDetail {
  sellable_quantity: number;
  limiting_component_variant_id: string | null;
  components: AvailabilityLeaf[];
}

interface DirectComponentRow {
  id: string;
  component_variant_id: string;
  qty: number;
  position: number;
  variants?: {
    sku: string | null;
    option_values: Record<string, string> | null;
    products?: { name: string } | null;
  } | null;
}

interface PickerVariantRow {
  id: string;
  sku: string | null;
  option_values: Record<string, string> | null;
  product_id: string;
  products?: { name: string; product_type: string | null } | null;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

export default function BundleTab({ orgId, parentVariantId, onChanged }: Props) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ component_variant_id: string; qty: number }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [picker, setPicker] = useState("");
  const [addId, setAddId] = useState("");

  const detailQuery = useQuery({
    queryKey: ["bundle-availability", orgId, parentVariantId],
    enabled: !!orgId && !!parentVariantId,
    queryFn: async () => {
      return productApi.getBundleAvailability(orgId, parentVariantId);
    },
  });

  const componentsQuery = useQuery({
    queryKey: ["bundle-components", orgId, parentVariantId],
    enabled: !!orgId && !!parentVariantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bundle_components")
        .select("id, component_variant_id, qty, position, variants(sku, option_values, products(name))")
        .eq("org_id", orgId)
        .eq("parent_variant_id", parentVariantId)
        .eq("is_active", true)
        .order("position");
      if (error) throw error;
      return (data ?? []).map((row) => {
        const variant = firstRelation(row.variants);
        return {
          ...row,
          variants: variant
            ? {
                ...variant,
                products: firstRelation(variant.products),
              }
            : null,
        };
      }) as DirectComponentRow[];
    },
  });

  const pickerQuery = useQuery({
    queryKey: ["bundle-picker-variants", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("variants")
        .select("id, sku, option_values, product_id, products(name, product_type)")
        .eq("org_id", orgId)
        .order("position");
      if (error) throw error;
      return (data ?? []).map((row) => ({ ...row, products: firstRelation(row.products) })) as PickerVariantRow[];
    },
  });

  const directComponents = componentsQuery.data ?? [];
  const detail = detailQuery.data;
  const leaves = detail?.components ?? [];

  const labelFor = (row: DirectComponentRow) => {
    const name = row.variants?.products?.name ?? "—";
    const opt = variantLabel(row.variants?.option_values);
    return opt ? `${name} · ${opt}` : name;
  };

  const pickerOptions = useMemo(() => {
    const inDraft = new Set(draft.map((d) => d.component_variant_id));
    return (pickerQuery.data ?? []).filter(
      (v) => v.id !== parentVariantId && !inDraft.has(v.id),
    );
  }, [pickerQuery.data, draft, parentVariantId]);

  const startEdit = () => {
    setDraft(
      directComponents.map((c) => ({ component_variant_id: c.component_variant_id, qty: c.qty })),
    );
    setError("");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft([]);
    setError("");
    setAddId("");
    setPicker("");
  };

  const updateQty = (idx: number, qty: number) =>
    setDraft((d) => d.map((line, i) => (i === idx ? { ...line, qty } : line)));

  const removeDraft = (idx: number) =>
    setDraft((d) => d.filter((_, i) => i !== idx));

  const addDraft = () => {
    if (!addId) return;
    const row = pickerOptions.find((v) => v.id === addId);
    if (!row) return;
    setDraft((d) => [...d, { component_variant_id: row.id, qty: 1 }]);
    setAddId("");
  };

  const save = async () => {
    setError("");
    if (draft.some((d) => !d.component_variant_id || Number(d.qty) <= 0)) {
      setError("Every component needs a valid variant and a positive quantity.");
      return;
    }
    setSaving(true);
    try {
      await productApi.setBundleComponents(
        orgId,
        parentVariantId,
        draft.map((d) => ({ component_variant_id: d.component_variant_id, qty: Number(d.qty) })),
      );
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setEditing(false);
    queryClient.invalidateQueries({ queryKey: ["bundle-components", orgId, parentVariantId] });
    queryClient.invalidateQueries({ queryKey: ["bundle-availability", orgId, parentVariantId] });
    onChanged();
  };

  const totalRequiredUnits = leaves.reduce((s, l) => s + l.qty_per_unit, 0);

  return (
    <div className="space-y-6">
      {/* Sellable availability summary */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Sellable availability</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Computed from component stock — a bundle holds no inventory of its own.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-700">{detail?.sellable_quantity ?? 0}</p>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">can be sold</p>
            </div>
          </div>
        </div>
        {leaves.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            No active components yet — add components to make this bundle sellable.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Component</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium text-right">Qty per bundle</th>
                  <th className="px-4 py-3 font-medium text-right">Available</th>
                  <th className="px-4 py-3 font-medium text-right">Bundles possible</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {leaves.map((l) => {
                  const isLimiting = l.component_variant_id === detail?.limiting_component_variant_id;
                  return (
                    <tr key={l.component_variant_id} className={isLimiting ? "bg-amber-50/60" : ""}>
                      <td className="px-4 py-3">
                        <span className="text-slate-800 font-medium">{l.product_name ?? "—"}</span>
                        {isLimiting && (
                          <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">
                            limiting
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{l.sku ?? "—"}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{l.qty_per_unit}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{l.available}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{l.units_possible}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {detail?.limiting_component_variant_id && leaves.length > 0 && (
          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
            The <span className="font-medium text-amber-700">limiting component</span> is the one that caps how many bundles can be sold. Top it up to sell more.
          </div>
        )}
      </div>

      {/* BOM editor */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Bill of materials</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {directComponents.length} component{directComponents.length === 1 ? "" : "s"} ·{" "}
              {totalRequiredUnits} unit{totalRequiredUnits === 1 ? "" : "s"} per bundle
            </p>
          </div>
          {!editing ? (
            <button
              onClick={startEdit}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-pencil-line" /> Edit components
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={cancelEdit}
                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-check-line" /> {saving ? "Saving..." : "Save BOM"}
              </button>
            </div>
          )}
        </div>

        {error && <div className="mx-4 mt-3 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

        {!editing ? (
          directComponents.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              No components yet. Click &quot;Edit components&quot; to build the kit.
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {directComponents.map((c) => (
                <div key={c.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{labelFor(c)}</p>
                    <p className="text-xs text-slate-400 font-mono">{c.variants?.sku ?? "—"}</p>
                  </div>
                  <div className="shrink-0 text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">×{c.qty}</span> per bundle
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  value={picker}
                  onChange={(e) => setPicker(e.target.value)}
                  placeholder="Search components..."
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
              <select
                value={addId}
                onChange={(e) => setAddId(e.target.value)}
                className="w-72 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Add a component...</option>
                {pickerOptions
                  .filter((v) => {
                    if (!picker.trim()) return true;
                    const q = picker.toLowerCase();
                    return (
                      (v.products?.name ?? "").toLowerCase().includes(q) ||
                      (v.sku ?? "").toLowerCase().includes(q)
                    );
                  })
                  .map((v) => {
                    const opt = variantLabel(v.option_values);
                    const isBundle = v.products?.product_type === "bundle";
                    return (
                      <option key={v.id} value={v.id}>
                        {v.products?.name ?? "—"}
                        {opt ? ` · ${opt}` : ""} ({v.sku ?? "—"}){isBundle ? " [Bundle]" : ""}
                      </option>
                    );
                  })}
              </select>
              <button
                onClick={addDraft}
                disabled={!addId}
                className="px-3 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-add-line" /> Add
              </button>
            </div>

            {draft.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
                No components. Use the picker above to add sellable variants to this bundle.
              </div>
            ) : (
              <div className="space-y-2">
                {draft.map((d, idx) => {
                  const row = (pickerQuery.data ?? []).find((v) => v.id === d.component_variant_id);
                  const name = row
                    ? `${row.products?.name ?? "—"}${variantLabel(row.option_values) ? ` · ${variantLabel(row.option_values)}` : ""}`
                    : "Unknown variant";
                  const sku = row?.sku ?? "—";
                  return (
                    <div key={idx} className="flex items-center gap-3 rounded-lg border border-slate-100 p-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
                        <p className="text-xs text-slate-400 font-mono">{sku}</p>
                      </div>
                      <label className="text-xs text-slate-500 shrink-0">qty</label>
                      <input
                        type="number"
                        min="1"
                        value={d.qty}
                        onChange={(e) => updateQty(idx, Number(e.target.value))}
                        className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => removeDraft(idx)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-slate-400">
              Components may themselves be bundles (nested kits); circular references are rejected. Removing a component here never changes past orders — each order keeps its own snapshot.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
